'use client'

// Visitors renderer — draws whatever VISITOR entities the sim currently
// publishes (world/visitors/ on the server). Presence is entirely the
// sim's call: when a visitor's budget runs out the entity leaves the
// broadcast and the mesh goes with it, so "it vanished and never came
// back" needs no client logic at all.
//
// Art comes from lib/world/visitorModels.js per kind; a kind with no
// model yet gets the placeholder, so the sim side of an experiment can
// go live before the art does.

import { useMemo, useRef, Suspense } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import useGameStore from '../../../stores/gameStore'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useTerrainHeight } from '../terrain/TerrainHeightProvider'
import { VISITOR_MODELS } from '../../../lib/world/visitorModels'
import { OctopusTentacles } from './OctopusTentacles'

// Kinds with a bespoke procedural visual. Checked before the GLB
// manifest: the octopus is shader-built on purpose (one instanced draw,
// all motion on the GPU), so a model in the manifest never overrides it.
const KIND_VISUALS = {
  octopus: OctopusTentacles,
}

export function Visitors() {
  // Re-render only when the visitor set meaningfully changes; entity
  // deltas arrive constantly for food/nests and none of that is ours.
  const entityStates = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates,
    (prev, next) => {
      // single pass, no arrays: this runs on every entity delta (~10Hz)
      // and used to allocate two filtered copies just to say "no change"
      let i = 0
      let j = 0
      for (;;) {
        while (i < prev.length && prev[i].type !== 'VISITOR') i++
        while (j < next.length && next[j].type !== 'VISITOR') j++
        const a = i < prev.length ? prev[i] : null
        const b = j < next.length ? next[j] : null
        if (!a && !b) return true
        if (!a || !b) return false
        if (a.id !== b.id || a.state?.usesLeft !== b.state?.usesLeft) return false
        i++
        j++
      }
    },
  )

  const visitors = useMemo(
    () => entityStates.filter((e) => e.type === 'VISITOR'),
    [entityStates],
  )

  const terrainField = useTerrainHeight()

  if (visitors.length === 0) return null

  return (
    <group>
      {visitors.map((entity) => {
        const groundY = terrainField ? terrainField.sample(entity.pos.x, entity.pos.z) : null
        const y = Number.isFinite(groundY) ? groundY : (entity.pos.y ?? 0)
        const Visual = KIND_VISUALS[entity.state?.kind] || null
        const modelUrl = VISITOR_MODELS[entity.state?.kind] || null
        return (
          <group key={entity.id} position={[entity.pos.x, y, entity.pos.z]}>
            {Visual ? (
              <Visual entity={entity} groundY={y} />
            ) : modelUrl ? (
              <Suspense fallback={<PlaceholderVisitor />}>
                <VisitorModel url={modelUrl} />
              </Suspense>
            ) : (
              <PlaceholderVisitor />
            )}
          </group>
        )
      })}
    </group>
  )
}

/**
 * One visitor, staged by hand — for the director's set, where no sim
 * publishes entities. Kind resolves through the same registries as the
 * live renderer above, so new visitor art appears on the director's set
 * the day it lands here, no second list to remember.
 */
export function StaticVisitor({ kind, position, usesLeft = 5 }) {
  const entity = useMemo(() => ({
    id: `static_${kind}`,
    pos: { x: position[0], y: position[1] ?? 0, z: position[2] },
    state: { kind, usesLeft },
  }), [kind, position, usesLeft])
  const Visual = KIND_VISUALS[kind] || null
  const modelUrl = VISITOR_MODELS[kind] || null
  return (
    <group position={[entity.pos.x, entity.pos.y, entity.pos.z]}>
      {Visual ? (
        <Visual entity={entity} groundY={entity.pos.y} />
      ) : modelUrl ? (
        <Suspense fallback={<PlaceholderVisitor />}>
          <VisitorModel url={modelUrl} />
        </Suspense>
      ) : (
        <PlaceholderVisitor />
      )}
    </group>
  )
}

function VisitorModel({ url }) {
  const { scene } = useGLTF(url)
  const cloned = useMemo(() => scene.clone(), [scene])
  const ref = useRef()
  // The same slow breathing bob as the placeholder, so swapping the art
  // in does not change how alive the thing reads from a distance.
  useFrame(({ clock }) => {
    if (ref.current) ref.current.position.y = Math.sin(clock.elapsedTime * 0.8) * 0.06
  })
  return <group ref={ref}><primitive object={cloned} /></group>
}

// A dark shape with eyes, sitting low in the water. Deliberately simple:
// it marks the spot and moves like something alive, and the real model
// replaces it via visitorModels.js without touching this file.
function PlaceholderVisitor() {
  const ref = useRef()
  useFrame(({ clock }) => {
    if (!ref.current) return
    const t = clock.elapsedTime
    ref.current.position.y = Math.sin(t * 0.8) * 0.06
    ref.current.rotation.y = Math.sin(t * 0.23) * 0.35
  })
  return (
    <group ref={ref}>
      {/* the head, half under */}
      <mesh position={[0, 0.28, 0]} scale={[1, 0.82, 1]}>
        <sphereGeometry args={[0.55, 24, 18]} />
        <meshStandardNodeMaterial color="#3d2f63" roughness={0.55} />
      </mesh>
      {/* eyes, the part that reads as a creature */}
      <mesh position={[0.2, 0.42, 0.42]}>
        <sphereGeometry args={[0.09, 12, 10]} />
        <meshStandardNodeMaterial color="#f6efe2" roughness={0.4} />
      </mesh>
      <mesh position={[-0.2, 0.42, 0.42]}>
        <sphereGeometry args={[0.09, 12, 10]} />
        <meshStandardNodeMaterial color="#f6efe2" roughness={0.4} />
      </mesh>
      <mesh position={[0.2, 0.42, 0.5]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardNodeMaterial color="#1d1633" roughness={0.3} />
      </mesh>
      <mesh position={[-0.2, 0.42, 0.5]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardNodeMaterial color="#1d1633" roughness={0.3} />
      </mesh>
      {/* two lazy arms breaking the surface */}
      <mesh position={[0.55, 0.08, -0.15]} rotation={[0, 0, -0.9]} scale={[1, 0.5, 1]}>
        <torusGeometry args={[0.22, 0.07, 8, 12, Math.PI]} />
        <meshStandardNodeMaterial color="#3d2f63" roughness={0.55} />
      </mesh>
      <mesh position={[-0.5, 0.06, 0.2]} rotation={[0, 1.2, 0.9]} scale={[1, 0.5, 1]}>
        <torusGeometry args={[0.18, 0.06, 8, 12, Math.PI]} />
        <meshStandardNodeMaterial color="#3d2f63" roughness={0.55} />
      </mesh>
    </group>
  )
}

export default Visitors
